import {useState, useRef, useEffect} from 'react'
import {Typography, TypographyProps} from '@viptech/react-components'

type Props = TypographyProps & {
  children: string
  scrollSpeed?: number
}

export default function SlideTypography({children, scrollSpeed = 1.5, ...rest}: Props) {
  const [isHovered, setIsHovered] = useState(false)
  const [textWidth, setTextWidth] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)

  const scrollInterval = useRef<number | null>(null)

  useEffect(() => {
    if (containerRef.current && textRef.current) {
      setContainerWidth(containerRef.current.offsetWidth)

      setTextWidth(textRef.current.offsetWidth / 2)
    }
  }, [isHovered, containerRef, textRef])

  const startAnimation = () => {
    if (!textRef.current || !containerRef.current) return

    scrollInterval.current = window.setInterval(() => {
      if (textRef.current) {
        const currentTransform = parseFloat(getComputedStyle(textRef.current).transform.split(',')[4] || '0')

        const newTransform = currentTransform <= -textWidth ? 0 : currentTransform - scrollSpeed

        textRef.current.style.transform = `translateX(${newTransform}px)`
      }
    }, 32)
  }

  const stopAnimation = () => {
    if (scrollInterval.current) {
      clearInterval(scrollInterval.current)
      scrollInterval.current = null
    }
    if (textRef.current) {
      textRef.current.style.transform = 'translateX(0)'
    }
  }
  const shouldScroll = textWidth * 2 > containerWidth

  return (
    <div
      ref={containerRef}
      style={{
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        position: 'relative',
        width: '100%',
        height: 'fit-content',
      }}
      onMouseEnter={() => {
        if (!shouldScroll) return
        setIsHovered(true)
        startAnimation()
      }}
      onMouseLeave={() => {
        setIsHovered(false)
        stopAnimation()
      }}>
      <div
        ref={textRef}
        style={{
          display: 'inline-block',
          transform: 'translateX(0)',
          willChange: 'transform',
        }}>
        <Typography {...rest}>{shouldScroll ? `${children} ${children}` : children}</Typography>
      </div>
    </div>
  )
}
